import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { RangerAvatar } from '../components/RangerAvatar';
import {
    Leaf,
    Trophy,
    Medal,
    Star,
    ArrowLeft,
    House,
    Target,
    Binoculars,
    BookOpen,
    Crown,
    TreePalm,
    Drop,
    Trash
} from '@phosphor-icons/react';

const AVATARS = {
    koala: '🐨',
    kangaroo: '🦘',
    wombat: '🐻',
    platypus: '🦆',
    emu: '🐦'
};

export default function LeaderboardPage() {
    const { api } = useAuth();
    const [leaderboard, setLeaderboard] = useState([]);
    const [impact, setImpact] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = async () => {
        try {
            const [leaderboardRes, impactRes] = await Promise.all([
                api().get('/leaderboard?limit=20'),
                api().get('/impact')
            ]);
            setLeaderboard(leaderboardRes.data);
            setImpact(impactRes.data);
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const getRankStyle = (index) => {
        if (index === 0) return 'bg-[#fde047] border-[#1e293b]';
        if (index === 1) return 'bg-gray-200 border-[#1e293b]';
        if (index === 2) return 'bg-orange-200 border-[#1e293b]';
        return 'bg-white border-[#1e293b]/30';
    };

    const getRankIcon = (index) => {
        if (index === 0) return <Crown weight="fill" className="text-[#f59e0b]" size={28} />;
        if (index === 1) return <Medal weight="fill" className="text-gray-500" size={24} />;
        if (index === 2) return <Medal weight="fill" className="text-orange-500" size={24} />;
        return <span className="font-fredoka font-bold text-[#475569]">{index + 1}</span>;
    };

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                            <div className="w-10 h-10 rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center">
                                <Leaf weight="fill" className="text-white" size={20} />
                            </div>
                            <span className="font-fredoka text-xl font-bold text-[#1e293b] hidden sm:block">
                                Bush Rangers
                            </span>
                        </Link>
                        
                        <Link 
                            to="/dashboard"
                            className="flex items-center gap-2 text-[#475569] hover:text-[#22c55e] font-semibold"
                            data-testid="back-to-dashboard"
                        >
                            <ArrowLeft weight="bold" size={20} />
                            <span className="hidden sm:inline">Dashboard</span>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="pt-20 px-4 max-w-7xl mx-auto">
                {/* Header */}
                <motion.div 
                    className="mb-8 text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <Trophy weight="duotone" className="text-[#fde047] mx-auto mb-2" size={64} />
                    <h1 className="font-fredoka text-3xl md:text-4xl font-bold text-[#1e293b] mb-2">
                        Leaderboard
                    </h1>
                    <p className="text-[#475569] text-lg">
                        Top Rangers making a difference!
                    </p>
                </motion.div>

                {/* National Impact */}
                {impact && (
                    <motion.section 
                        className="mb-8"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-4 text-center">
                            Our National Impact
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="border-4 border-[#1e293b] rounded-2xl p-4 text-center shadow-[4px_4px_0_0_#1e293b]" style={{ backgroundColor: '#22c55e' }}>
                                <TreePalm weight="duotone" className="text-white mx-auto mb-2" size={32} />
                                <p className="font-fredoka text-2xl md:text-3xl font-bold text-white">
                                    {impact.total_trees_planted}
                                </p>
                                <p className="text-white text-sm opacity-80">Trees Planted</p>
                            </div>
                            <div className="border-4 border-[#1e293b] rounded-2xl p-4 text-center shadow-[4px_4px_0_0_#1e293b]" style={{ backgroundColor: '#38bdf8' }}>
                                <Drop weight="duotone" className="text-white mx-auto mb-2" size={32} />
                                <p className="font-fredoka text-2xl md:text-3xl font-bold text-white">
                                    {impact.total_water_saved}L
                                </p>
                                <p className="text-white text-sm opacity-80">Water Saved</p>
                            </div>
                            <div className="border-4 border-[#1e293b] rounded-2xl p-4 text-center shadow-[4px_4px_0_0_#1e293b]" style={{ backgroundColor: '#fde047' }}>
                                <Trash weight="duotone" className="text-[#1e293b] mx-auto mb-2" size={32} />
                                <p className="font-fredoka text-2xl md:text-3xl font-bold text-[#1e293b]">
                                    {impact.total_rubbish_collected}
                                </p>
                                <p className="text-[#1e293b] text-sm opacity-80">Rubbish Items</p>
                            </div>
                            <div className="border-4 border-[#1e293b] rounded-2xl p-4 text-center shadow-[4px_4px_0_0_#1e293b]" style={{ backgroundColor: '#fb7185' }}>
                                <Star weight="fill" className="text-white mx-auto mb-2" size={32} />
                                <p className="font-fredoka text-2xl md:text-3xl font-bold text-white">
                                    {impact.total_missions_completed}
                                </p>
                                <p className="text-white text-sm opacity-80">Missions Done</p>
                            </div>
                        </div>
                    </motion.section>
                )}

                {/* Leaderboard */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-4">
                        Top Rangers
                    </h2>
                    
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="w-16 h-16 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="font-fredoka text-lg">Loading leaderboard...</p>
                        </div>
                    ) : leaderboard.length === 0 ? (
                        <div className="brutalist-card p-8 text-center">
                            <Trophy weight="duotone" className="text-[#475569] mx-auto mb-4" size={48} />
                            <h3 className="font-fredoka text-xl font-bold text-[#1e293b] mb-2">
                                No Rangers Yet
                            </h3>
                            <p className="text-[#475569]">
                                Be the first to complete missions and join the leaderboard!
                            </p>
                        </div>
                    ) : (
                        <div className="brutalist-card overflow-hidden">
                            <div className="divide-y-4 divide-[#1e293b]/10">
                                {leaderboard.map((ranger, index) => (
                                    <motion.div
                                        key={ranger.id}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.05 * index }}
                                        className={`flex items-center gap-4 p-4 ${getRankStyle(index)}`}
                                        data-testid={`leaderboard-row-${index}`}
                                    >
                                        {/* Rank */}
                                        <div className="w-10 h-10 flex items-center justify-center">
                                            {getRankIcon(index)}
                                        </div>
                                        
                                        {/* Avatar */}
                                        <div className={`w-12 h-12 rounded-full overflow-hidden border-4 border-[#1e293b] ${
                                            index < 3 ? 'bg-white' : 'bg-[#f1f5f9]'
                                        }`}>
                                            <RangerAvatar config={ranger.avatar_config} variant="badge" className="w-full h-full" data-testid={`leaderboard-avatar-${index}`} />
                                        </div>
                                        
                                        {/* Info */}
                                        <div className="flex-1 min-w-0">
                                            <p className="font-fredoka font-bold text-[#1e293b] truncate">
                                                {ranger.name}
                                            </p>
                                            <p className="text-sm text-[#475569]">
                                                {ranger.level}
                                            </p>
                                        </div>
                                        
                                        {/* Stats */}
                                        <div className="text-right">
                                            <div className="flex items-center gap-1 justify-end">
                                                <Star weight="fill" className="text-[#fde047]" size={20} />
                                                <span className="font-fredoka font-bold text-[#1e293b]">
                                                    {ranger.xp} XP
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1 justify-end text-sm text-[#475569]">
                                                <Medal weight="fill" size={16} />
                                                <span>{ranger.badges_count} badges</span>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    )}
                </motion.section>
            </main>
        </div>
    );
}
